import type { Hit } from 'instantsearch.js';

import { useDocument } from '@src/hooks/document';
import { useDocumentState } from '@src/hooks/documentState';
import { getDownloadUrl, getFileUrl } from './helpers';
import { navigate } from 'gatsby';
import icons from './Hit.Icons';
import { useIsMainSubscribed } from '@src/hooks/user';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';
import type { DocumentClickOutcome } from '@src/utils/trackDocumentClick';

const stateToOutcome = (documentState: string): DocumentClickOutcome => {
  switch (documentState) {
    case 'Open':
      return 'opened';
    case 'Purchased':
      return 'profile';
    case 'Buy':
      return 'detail';
    default:
      return 'cart';
  }
};

export const useResourceLink = (hit: Hit, page: boolean) => {
  const { handleSubscibeOrBuyClick } = useDocument(hit.requires_subscription, hit.id);
  const documentState = useDocumentState(hit.requires_subscription, hit.id);
  const isSubscribed = useIsMainSubscribed();
  const fileUrl = getFileUrl(hit?.file_url);
  const track = (outcome: DocumentClickOutcome, destinationUrl?: string) =>
    trackDocumentClick(hit, { source: 'search', outcome, isSubscribed, destinationUrl });

  // 1: Details page
  if (hit?.paths?.detail && hit?.has_password && !hit?.requires_subscription) {
    return {
      onClick: () => {
        track('detail', hit.paths.detail);
        navigate(hit.paths.detail);
      },
      icon: icons.locked,
    };
  }

  // 2: Subscription - (Basket and Subscription Icon)
  if (hit?.requires_subscription && hit?.has_password) {
    return {
      onClick: () => {
        track(stateToOutcome(documentState), hit?.paths?.detail);
        handleSubscibeOrBuyClick(hit.id, () => navigate(hit?.paths?.detail));
      },
      icon: undefined,
    };
  }

  // 3: Locked Only (Locked Icon)
  if (hit?.has_password && hit?.free_document) {
    const destinationUrl = getDownloadUrl(fileUrl, hit.id);
    return {
      onClick: () => {
        track('opened', destinationUrl);
        window.open(destinationUrl, '_self');
      },
      icon: icons.locked,
    };
  }

  // 4: Basket (Basket Icon)
  if (hit?.has_password) {
    return {
      onClick: () => {
        track(stateToOutcome(documentState), hit?.paths?.detail);
        handleSubscibeOrBuyClick(hit.id, () => navigate(hit?.paths?.detail));
      },
      icon: undefined,
    };
  }

  const destinationUrl = getDownloadUrl(fileUrl, hit.id);
  return {
    onClick: () => {
      track('opened', destinationUrl);
      window.open(destinationUrl, '_self');
    },
    icon: undefined,
  };
};

export const usePageLink = (hit: Hit, page: boolean) => {
  if (page) {
    return () => navigate(hit.slug);
  }

  if (hit?.paths?.module) {
    return () => navigate(hit.paths.module);
  }

  return null;
};
